import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SelectField,
  SubmitButton,
  useModalContext,
} from '@/components';
import { accountStatusFragment } from '@/components/AccountStatus';
import { useTranslate } from '@/contexts';
import { AccountStatusEnum, CloseAccountCauseV4 } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import { useCloseAccountCauses } from '@/utils';
import { assert } from '@/utils/error';
import {
  GlobalClosePlayerAccountStatusMutation,
  GlobalClosePlayerAccountStatusMutationVariables,
  GlobalPlayerAccountStatusFormQuery,
  GlobalPlayerAccountStatusFormQueryVariables,
} from './__generated__/GlobalPlayerAccountStatusForm';

const query = graphql`
  query SanityGlobalPlayerAccountStatusForm {
    sanityGlobalPlayerAccountStatusForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const globalPlayerAccountStatusPlayerFragment = gql`
  fragment GlobalPlayerAccountStatusPlayer on Player {
    id
    brand {
      id
      name
    }
    ...AccountStatusFragment
  }
  ${accountStatusFragment}
`;

const globalPlayerQuery = gql`
  query GlobalPlayerAccountStatusForm($playerGlobalId: ID!) {
    playerGlobal(playerGlobalId: $playerGlobalId) {
      id
      players {
        ...GlobalPlayerAccountStatusPlayer
      }
    }
  }
  ${globalPlayerAccountStatusPlayerFragment}
`;

const globalCloseMutation = gql`
  mutation GlobalClosePlayerAccountStatus(
    $playerGlobalId: ID!
    $closeCause: CloseAccountCauseV4!
  ) {
    closeGlobalPlayerAccounts(
      playerGlobalId: $playerGlobalId
      closeCause: $closeCause
    ) {
      id
      players {
        ...GlobalPlayerAccountStatusPlayer
      }
    }
  }
  ${globalPlayerAccountStatusPlayerFragment}
`;

const CloseGlobalAccountsForm: FC<{
  cause: CloseAccountCauseV4;
  playerGlobalId: string;
}> = ({ cause, playerGlobalId }) => {
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const closeAccountCauseOptions = useCloseAccountCauses();

  const [closeGlobalPlayerAccountState, closeGlobalPlayerAccount] = useMutation<
    GlobalClosePlayerAccountStatusMutation,
    GlobalClosePlayerAccountStatusMutationVariables
  >(globalCloseMutation);

  const { close } = useModalContext();

  const defaultValues = {
    closeCause: cause,
  };

  const onSubmit = (values: typeof defaultValues) => {
    closeGlobalPlayerAccount({
      playerGlobalId,
      closeCause: values.closeCause,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  return (
    <Form
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      className="grid grid-cols-2 sm:grid-cols-3 gap-6"
    >
      <SelectField
        title="Reason"
        name="closeCause"
        id="GlobalPlayerAccountStatusForm__closeCause"
        options={closeAccountCauseOptions}
      />
      <ErrorMessage message={errorMessage} />
      <SubmitButton
        value="Close accounts"
        disabled={closeGlobalPlayerAccountState.fetching}
      />
    </Form>
  );
};

const GlobalPlayerAccountStatusForm: FC<{
  playerGlobalId: string;
}> = ({ playerGlobalId }) => {
  const { t } = useTranslate();

  const staticData =
    useStaticQuery<Queries.SanityGlobalPlayerAccountStatusFormQuery>(query);

  const form = staticData.sanityGlobalPlayerAccountStatusForm;

  assert(form, 'missing form data');

  const [{ data, error }] = useQuery<
    GlobalPlayerAccountStatusFormQuery,
    GlobalPlayerAccountStatusFormQueryVariables
  >({
    query: globalPlayerQuery,
    variables: { playerGlobalId },
  });

  const players = data?.playerGlobal.players ?? [];

  const closedPlayer = players.find(
    (player) => player.accountStatus.status !== AccountStatusEnum.Open,
  );

  const cause = closedPlayer?.accountStatus.cause as CloseAccountCauseV4;

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        {error && <ErrorMessage message={error.message} />}
        {data && (
          <div className="grid gap-6">
            <CloseGlobalAccountsForm
              cause={cause}
              playerGlobalId={playerGlobalId}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export default GlobalPlayerAccountStatusForm;
